import React from 'react';
import { Link } from 'react-router-dom';
import { X, Bell, Trash2 } from 'lucide-react';
import { Button } from '../shared';
import { formatDate, formatTime } from '../../utils/dateUtils';

const formatType = (type) =>
 type ? String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const DetailRow = ({ label, children }) => (
 <div>
 <p className="text-sm font-medium text-gray-500">{label}</p>
 <div className="mt-0.5 text-base text-gray-900">{children}</div>
 </div>
);

const AdminNotificationDetailModal = ({
 notification,
 onClose,
 onDelete,
 isDeleting = false,
}) => {
 if (!notification) return null;

 const handleDelete = () => {
 if (window.confirm('Are you sure you want to delete this notification?')) {
 onDelete?.(notification.id);
 }
 };

 const recipientLabel = notification.is_broadcast
 ? `Broadcast${notification.target_user_type ? ` (${notification.target_user_type})` : ''}`
 : notification.user?.name || 'All users';

 const userProfilePath =
 notification.user?.id && notification.user?.user_type === 'client'
 ? `/admin/clients/${notification.user.id}`
 : notification.user?.id
 ? `/admin/vendors/${notification.user.id}`
 : null;

 return (
 <div
 className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
 onClick={onClose}
 role="dialog"
 aria-modal="true"
 aria-labelledby="notification-detail-title"
 >
 <div
 className="bg-surface rounded-xl shadow-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="p-6">
 <div className="flex justify-between items-start gap-4 mb-6">
 <div className="flex items-start gap-3 min-w-0">
 <div className="flex-shrink-0 w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center">
 <Bell className="w-5 h-5 text-ink" />
 </div>
 <div className="min-w-0">
 <h2 id="notification-detail-title" className="text-xl font-bold text-gray-900 break-words">
 {notification.title || 'Notification'}
 </h2>
 <div className="flex flex-wrap gap-2 mt-2">
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ notification.is_read ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800' }`}
 >
 {notification.is_read ? 'Read' : 'Unread'}
 </span>
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ notification.is_sent ? 'bg-surface-muted text-ink' : 'bg-gray-100 text-gray-800' }`}
 >
 {notification.is_sent ? 'Sent' : 'Pending'}
 </span>
 {notification.is_broadcast && (
 <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-surface-muted text-ink">
 Broadcast
 </span>
 )}
 </div>
 </div>
 </div>
 <button
 type="button"
 onClick={onClose}
 className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
 aria-label="Close"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 <div className="space-y-4">
 <DetailRow label="Type">{formatType(notification.type)}</DetailRow>

 <DetailRow label="Recipient">
 {notification.user?.id && !notification.is_broadcast ? (
 <div>
 <p className="font-medium">{notification.user.name}</p>
 <p className="text-sm text-gray-600">{notification.user.email}</p>
 {notification.user.user_type && (
 <p className="text-xs text-gray-500 capitalize mt-0.5">
 {notification.user.user_type}
 </p>
 )}
 {userProfilePath && (
 <Link
 to={userProfilePath}
 className="text-sm text-ink hover:underline font-medium mt-1 inline-block"
 onClick={onClose}
 >
 View user profile
 </Link>
 )}
 </div>
 ) : (
 <span>{recipientLabel}</span>
 )}
 </DetailRow>

 <DetailRow label="Created">
 {formatDate(notification.created_at, 'long')} at {formatTime(notification.created_at)}
 </DetailRow>

 {notification.sent_at && (
 <DetailRow label="Sent at">
 {formatDate(notification.sent_at, 'long')} at {formatTime(notification.sent_at)}
 </DetailRow>
 )}

 {notification.read_at && (
 <DetailRow label="Read at">
 {formatDate(notification.read_at, 'long')} at {formatTime(notification.read_at)}
 </DetailRow>
 )}

 {notification.expires_at && (
 <DetailRow label="Expires">
 {formatDate(notification.expires_at, 'long')} at {formatTime(notification.expires_at)}
 </DetailRow>
 )}

 <div>
 <p className="text-sm font-medium text-gray-500 mb-2">Message</p>
 <div className="bg-surface-muted rounded-lg p-4 text-gray-900 whitespace-pre-wrap break-words text-sm leading-relaxed">
 {notification.message || '—'}
 </div>
 </div>

 {notification.data && Object.keys(notification.data).length > 0 && (
 <div>
 <p className="text-sm font-medium text-gray-500 mb-2">Additional data</p>
 <pre className="bg-surface-muted rounded-lg p-4 text-xs text-gray-800 overflow-x-auto">
 {JSON.stringify(notification.data, null, 2)}
 </pre>
 </div>
 )}

 {notification.id && (
 <DetailRow label="Notification ID">
 <span className="font-mono text-sm text-gray-600 break-all">{notification.id}</span>
 </DetailRow>
 )}
 </div>

 <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6 pt-6 border-t border-gray-200">
 <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
 Close
 </Button>
 <Button
 variant="danger"
 onClick={handleDelete}
 disabled={isDeleting}
 className="w-full sm:w-auto flex items-center justify-center gap-2"
 >
 <Trash2 className="w-4 h-4" />
 {isDeleting ? 'Deleting…' : 'Delete'}
 </Button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default AdminNotificationDetailModal;
